import { queryKeys } from './queryKeys';
import { WebSocketTopic } from '../types/websocket';

/**
 * THUB-182: map a backend broadcast to the MINIMAL set of query keys it
 * actually invalidates.
 *
 * Context — two things the ticket description gets wrong:
 *   1. The app was NOT under-subscribed. App.tsx already subscribed to all
 *      SEVEN backend topics. Server-side filtering is by TOPIC only
 *      (shared/websocket_utils.py get_active_connections), and the wire
 *      envelope's routing key is the topic, so the app already received every
 *      broadcast the backend emits.
 *   2. There is no set of "~50 ignored events". The backend has exactly 7
 *      topics / 21 event-type strings.
 *
 * The real defect was the opposite of under-subscription: six of the seven
 * handlers collapsed into one blunt `refreshData()` that invalidated all four
 * query keys on every single broadcast. `file_ready` fires once per file
 * (file_processing_workflow), so a 50-file inbound drop produced 50 x 4 = 200
 * refetches, in every connected browser simultaneously. `staleTime` does NOT
 * protect against this — `invalidateQueries` marks queries stale
 * unconditionally and refetches every mounted observer immediately.
 *
 * This module is pure (no React) so the mapping is directly unit-testable.
 */

/** A single query key, as accepted by `invalidateQueries({ queryKey })`. */
export type InvalidationKey = readonly unknown[];

/** Stable string identity for a query key, used to de-duplicate a batch. */
export function keyId(key: InvalidationKey): string {
  return JSON.stringify(key);
}

/**
 * Topics App.tsx subscribes to for cache invalidation.
 *
 * Deliberately EXCLUDES:
 *  - `role_mapping_changes` and `policy_changes` — neither maps to any of the
 *    four query keys (policies and role mappings are not in `queryKeys` at
 *    all), and both already have dedicated local subscribers that do the real
 *    work: contexts/RoleContext.tsx, components/admin/AdminPanel.tsx and
 *    components/admin/PolicyManagement.tsx. Routing them through App.tsx only
 *    triggered four pointless app-wide refetches each.
 *  - `analytics_updates` — dead in every deployed environment: analytics_handler
 *    gates its broadcast on WEBSOCKET_API_ENDPOINT, which CDK never sets on the
 *    analytics Lambda (api-nested-stack.ts sets it for partner/connection/task/
 *    roleMapping/policyManagement but not analyticsLambda). It would also fire
 *    on every dashboard GET — a read, not a mutation — so enabling it invites a
 *    refetch loop. Left OFF deliberately; see the PR body follow-up.
 */
export const INVALIDATING_TOPICS = [
  'partner_status_changes',
  'connection_status_changes',
  'task_status_changes',
  'execution_updates',
] as const satisfies readonly WebSocketTopic[];

export type InvalidatingTopic = typeof INVALIDATING_TOPICS[number];

/**
 * Given a topic and the broadcast's inner `data` payload, return the query keys
 * to invalidate.
 *
 * `data` is the UNWRAPPED inner object: WebSocketContext dispatches
 * `dispatchBroadcast(msg.topic, msg.data)`, so the event name lives at
 * `data.eventType` for the five typed wrappers.
 */
export function keysForBroadcast(topic: string, data?: any): InvalidationKey[] {
  const eventType: string | undefined = data?.eventType;

  switch (topic) {
    case 'partner_status_changes': {
      // partner_deleted cascades to that partner's connections and tasks
      // (partner_management_api / offboard_partner delete children).
      if (eventType === 'partner_deleted') {
        return [queryKeys.partners.all, queryKeys.connections.all, queryKeys.tasks.all];
      }
      return [queryKeys.partners.all];
    }

    case 'connection_status_changes': {
      // connection_deleted cascades to that connection's tasks.
      if (eventType === 'connection_deleted') {
        return [queryKeys.connections.all, queryKeys.tasks.all];
      }
      return [queryKeys.connections.all];
    }

    case 'task_status_changes': {
      // THUB-180 integration seam: taskCounts is a field on the CONNECTION (and
      // PARTNER) record, recomputed server-side on task create/delete/enable/
      // disable/reassign. Invalidating only tasks.all would leave the
      // freshly-corrected count sitting in DynamoDB while the browser renders
      // the old one, so connections.all and partners.all are required here.
      return [queryKeys.tasks.all, queryKeys.connections.all, queryKeys.partners.all];
    }

    case 'execution_updates': {
      // Highest-volume topic — `file_ready` is emitted once per file. Only
      // task-shaped data changes; it must NEVER invalidate partners,
      // connections or analytics (which is what the old blanket handler did).
      return [queryKeys.tasks.all];
    }

    default:
      return [];
  }
}
